import { computed, ref } from 'vue';
import type { ComputedRef, Ref } from 'vue';

import type { ChildAgeOption, TouristRoom, TouristsSelection } from './types';
import {
  MAX_ROOMS,
  MIN_ROOMS,
  addChild,
  addRoom,
  isValidTourists,
  normalizeTourists,
  removeChild,
  removeRoom,
  setChildAge,
  setRoomAdults,
  totalTourists,
} from '../lib/touristsRooms';
import { formatRoomsLabel, formatTouristsValue } from '../lib/formatTourists';
import { CHILD_AGE_OPTIONS } from '../lib/childAgeOptions';

export interface UseTouristsRooms {
  rooms: Ref<TouristRoom[]>;
  controlActivated: Ref<boolean>;

  selection: ComputedRef<TouristsSelection>;
  roomsLabel: ComputedRef<string>;
  touristsValue: ComputedRef<string>;
  total: ComputedRef<number>;
  canAddRoom: ComputedRef<boolean>;
  canRemoveRoom: ComputedRef<boolean>;
  ageOptions: ChildAgeOption[];

  open: () => void;
  close: () => void;
  toggle: () => void;
  setAdults: (roomIndex: number, value: number) => void;
  addChild: (roomIndex: number, age: number) => void;
  setChildAge: (roomIndex: number, childIndex: number, age: number) => void;
  removeChild: (roomIndex: number, childIndex: number) => void;
  addRoom: () => void;
  removeRoom: (roomIndex: number) => void;
  restore: (sel: TouristsSelection | null) => void;
  validate: () => boolean;
}

export interface UseTouristsRoomsOptions {
  initial?: TouristsSelection | null;
  isVisibleAddButton: boolean; 
}

export function useTouristsRooms(options: UseTouristsRoomsOptions): UseTouristsRooms {
  const initial = normalizeTourists(options?.initial ?? null);

  const rooms = ref<TouristRoom[]>(initial.rooms);
  const controlActivated = ref(false);

  const selection = computed<TouristsSelection>(() => ({ rooms: rooms.value }));
  const total = computed<number>(() => totalTourists(selection.value));
  const roomsLabel = computed<string>(() => formatRoomsLabel(rooms.value.length, options.isVisibleAddButton));
  const touristsValue = computed<string>(() => formatTouristsValue(total.value, options.isVisibleAddButton, rooms.value));
  const canAddRoom = computed<boolean>(() => rooms.value.length < MAX_ROOMS);
  const canRemoveRoom = computed<boolean>(() => rooms.value.length > MIN_ROOMS);

  const open = (): void => {
    controlActivated.value = true;
  };

  const close = (): void => {
    controlActivated.value = false;
  };

  const toggle = (): void => {
    controlActivated.value = !controlActivated.value;
  };

  const setAdults = (roomIndex: number, value: number): void => {
    rooms.value = setRoomAdults(selection.value, roomIndex, value).rooms;
  };

  const addChildAction = (roomIndex: number, age: number): void => {
    rooms.value = addChild(selection.value, roomIndex, age).rooms;
  };

  const setChildAgeAction = (roomIndex: number, childIndex: number, age: number): void => {
    rooms.value = setChildAge(selection.value, roomIndex, childIndex, age).rooms;
  };

  const removeChildAction = (roomIndex: number, childIndex: number): void => {
    rooms.value = removeChild(selection.value, roomIndex, childIndex).rooms;
  };

  const addRoomAction = (): void => {
    rooms.value = addRoom(selection.value).rooms;
  };

  const removeRoomAction = (roomIndex: number): void => {
    rooms.value = removeRoom(selection.value, roomIndex).rooms;
  };

  const restore = (sel: TouristsSelection | null): void => {
    rooms.value = normalizeTourists(sel).rooms;
    controlActivated.value = false;
  };

  const validate = (): boolean => isValidTourists(selection.value);

  return {
    rooms,
    controlActivated,
    selection,
    roomsLabel,
    touristsValue,
    total,
    canAddRoom,
    canRemoveRoom,
    ageOptions: CHILD_AGE_OPTIONS,
    open,
    close,
    toggle,
    setAdults,
    addChild: addChildAction,
    setChildAge: setChildAgeAction,
    removeChild: removeChildAction,
    addRoom: addRoomAction,
    removeRoom: removeRoomAction,
    restore,
    validate,
  };
}
